import React from "react";
import { useQuery } from "wasp/client/operations";
import { getJobById, deleteJob } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
} from "../../components/layout";
import {
  DollarSign,
  Tag,
  Phone,
  Edit,
  AlertCircle,
  Clock,
  Star,
  Award,
  CheckCircle,
  Trash2,
  Mail,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";
import { useAction } from "wasp/client/operations";

export default function ViewJobPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const deleteJobFn = useAction(deleteJob);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Fetch the job data
  const {
    data: job,
    isLoading,
    error,
  } = useQuery(getJobById, { itemId: itemId! });

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/jobs`);
    } else {
      navigate("/jobs");
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this job? This action cannot be undone.",
      )
    )
      return;
    setIsDeleting(true);
    try {
      await deleteJobFn({ itemId: itemId! });
      if (selectedLocation?.locationCode) {
        navigate(`/locations/${selectedLocation.locationCode}/jobs`);
      } else {
        navigate("/jobs");
      }
    } catch (err) {
      alert("Failed to delete job. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">You must be logged in to view jobs.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading job...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !job) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-content-text mb-2">
              Job Not Found
            </h3>
            <p className="text-content-text-muted">
              The job you're looking for doesn't exist.
            </p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getPriceTypeColor = (priceType: string) => {
    const colors = {
      HOURLY: "bg-blue-100 text-blue-800",
      FIXED: "bg-green-100 text-green-800",
      PER_SESSION: "bg-purple-100 text-purple-800",
      FREE: "bg-gray-100 text-gray-800",
    };
    return (
      colors[priceType as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "Community",
      dropdownItems: [
        {
          label: "Buy and Sell",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/buy-sell`
            : "/buy-sell",
        },
        {
          label: "Food Court",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/food-court`
            : "/food-court",
        },
        {
          label: "Lost and Found",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/lost-found`
            : "/lost-found",
        },
        {
          label: "Retail",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/retail`
            : "/retail",
        },
        {
          label: "Jobs",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/jobs`
            : "/jobs",
        },
      ],
    },
    {
      label: "Jobs",
      isClickable: true,
      href: selectedLocation?.locationCode
        ? `/locations/${selectedLocation.locationCode}/jobs`
        : "/jobs",
    },
  ];

  const isAdmin = user?.isAdmin;
  const sidebarContent = (
    <>
      {isAdmin && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={
                selectedLocation?.locationCode
                  ? `/locations/${selectedLocation.locationCode}/jobs/${job.id}/edit`
                  : `/jobs/${job.id}/edit`
              }
              icon={Edit}
            >
              Edit Job
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
              disabled={isDeleting}
              icon={Trash2}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                  Deleting...
                </>
              ) : (
                "Delete Job"
              )}
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField
            label="Posted by"
            value={job.author?.name || job.author?.email || "N/A"}
          />
          <DetailField
            label="Posted on"
            value={new Date(job.createdAt).toLocaleDateString()}
          />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      {/* Job Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-gray-900">{job.title}</h2>
              <SubscribeButton contentId={job.id} />
            </div>
          </div>

          <div className="text-3xl font-bold text-green-600">
            {job.metadata?.priceType === "FREE"
              ? "Free"
              : job.metadata?.price
                ? `$${job.metadata.price}`
                : "Contact for Price"}
          </div>
        </div>

        <p className="text-gray-700 text-lg leading-relaxed">
          {job.description}
        </p>
      </div>

      {/* Job Details */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Job Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {job.metadata?.schedule && (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Schedule
                  </div>
                  <div className="text-gray-900 whitespace-pre-line">
                    {job.metadata.schedule}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-500">Pricing</div>
                <div className="text-gray-900">
                  {job.metadata?.priceType === "FREE"
                    ? "Free Job"
                    : job.metadata?.price
                      ? `$${job.metadata.price} ${job.metadata.priceType
                        ?.toLowerCase()
                        ?.replace("_", " ")}`
                      : "Contact for pricing"}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-gray-400 mt-0.5 flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">
                  Job Provider
                </div>
                <div className="text-gray-900">
                  {job.author.name || job.author.email}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-gray-400 mt-0.5 flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Listed</div>
                <div className="text-gray-900">
                  {new Date(job.createdAt).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      {(job.metadata?.email || job.metadata?.phone) && (
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Interested in this job?
          </h3>
          <p className="text-gray-600 mb-4">
            Contact the job provider to discuss your needs and arrange a
            meeting.
          </p>
          <div className="flex flex-col gap-2">
            {job.metadata?.email && (
              <div className="flex items-center gap-2 text-blue-600">
                <Mail className="w-4 h-4" />
                <a
                  href={`mailto:${job.metadata.email}`}
                  className="font-medium hover:underline"
                >
                  {job.metadata.email}
                </a>
              </div>
            )}
            {job.metadata?.phone && (
              <div className="flex items-center gap-2 text-blue-600">
                <Phone className="w-4 h-4" />
                <a
                  href={`tel:${job.metadata.phone}`}
                  className="font-medium hover:underline"
                >
                  {job.metadata.phone}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </IndividualViewPageLayout>
  );
}
